import React, { useState, useEffect } from "react";
import {
	Modal,
	Form,
	Select,
	DatePicker,
	Input,
	Button,
	message,
	Typography,
} from "antd";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";
import useIsMobile from "../../hooks/useIsMobile";

const { TextArea } = Input;
const { Option } = Select;

const EditCellModal = ({
	visible,
	onCancel,
	onSuccess,
	userId,
	currentCellId,
	userGender,
}) => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [districts, setDistricts] = useState([]);
	const [zones, setZones] = useState([]);
	const [cells, setCells] = useState([]);
	const [selectedDistrict, setSelectedDistrict] = useState(null);
	const [selectedMissionaryType, setSelectedMissionaryType] =
		useState(null);
	const [selectedZone, setSelectedZone] = useState(null);

	useEffect(() => {
		if (visible) {
			getDistricts();
			// Set missionary type based on user gender
			const missionaryType =
				userGender === "남" ? "남선" : "여선";
			form.setFieldsValue({
				changed_cell: currentCellId,
				changed_date: dayjs(),
				missionary_type: missionaryType,
			});
			setSelectedMissionaryType(missionaryType);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- form is stable
	}, [visible, currentCellId, userGender]);

	// Effect to load zones when district and missionary type are available
	useEffect(() => {
		if (selectedDistrict && selectedMissionaryType) {
			getZonesByDistrictAndMissionary(
				selectedDistrict,
				selectedMissionaryType
			);
		}
	}, [selectedDistrict, selectedMissionaryType]);

	const getDistricts = async () => {
		try {
			const response = await useAxios.get(
				"/district/districts/"
			);
			if (response.status === 200) {
				setDistricts(response.data);
			}
		} catch (error) {
			message.error("구역 목록을 불러오는데 실패했습니다.");
		}
	};

	const getZonesByDistrictAndMissionary = async (
		districtId,
		missionaryType
	) => {
		try {
			const response = await useAxios.get(
				`/district/zones-by-district-missionary/${districtId}/${missionaryType}/`
			);
			if (response.status === 200) {
				setZones(response.data);
			}
		} catch (error) {
			message.error("지구 목록을 불러오는데 실패했습니다.");
		}
	};

	const getCellsByZone = async (zoneId) => {
		try {
			const response = await useAxios.get(
				`/district/cells-by-zone/${zoneId}/`
			);
			if (response.status === 200) {
				setCells(response.data);
			}
		} catch (error) {
			message.error("구역 목록을 불러오는데 실패했습니다.");
		}
	};

	const handleDistrictChange = (districtId) => {
		setSelectedDistrict(districtId);
		setSelectedZone(null);
		setZones([]);
		setCells([]);
		form.setFieldsValue({
			zone: undefined,
			changed_cell: undefined,
		});
		// Missionary type will be automatically set based on gender, so we don't reset it
	};

	const handleZoneChange = (zoneId) => {
		setSelectedZone(zoneId);
		setCells([]);
		form.setFieldsValue({ changed_cell: undefined });

		if (zoneId) {
			getCellsByZone(zoneId);
		}
	};

	const handleSubmit = async () => {
		try {
			setLoading(true);
			const values = await form.validateFields();

			const selectedCell = cells.find(
				(c) => c.id === values.changed_cell
			);
			const selectedZone = zones.find(
				(z) => z.id === values.zone
			);
			const selectedDistrict = districts.find(
				(d) => d.id === values.district
			);

			const updateData = {
				cell_id: values.changed_cell,
				cell_change: {
					changed_date:
						values.changed_date.format(
							"YYYY-MM-DD"
						),
					changed_cell:
						selectedMissionaryType +
						"교회 " +
						selectedDistrict?.name +
						"대 교구 " +
						selectedZone?.name +
						"지구 " +
						selectedCell?.name +
						"구역",
					reason: values.reason,
				},
			};

			const response = await useAxios.put(
				`/users/update-cell/${userId}/`,
				updateData
			);

			if (response.status === 200) {
				message.success(
					"구역이 성공적으로 수정되었습니다."
				);
				onSuccess();
				form.resetFields();
			}
		} catch (error) {
			message.error("구역 수정에 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setSelectedDistrict(null);
		setSelectedMissionaryType(null);
		setSelectedZone(null);
		setZones([]);
		setCells([]);
		onCancel();
	};

	return (
		<Modal
			title="구역 수정"
			open={visible}
			onCancel={handleCancel}
			footer={[
				<Button key="cancel" onClick={handleCancel}>
					취소
				</Button>,
				<Button
					key="submit"
					type="primary"
					loading={loading}
					onClick={handleSubmit}>
					수정
				</Button>,
			]}
			width={500}>
			<Form form={form} layout="vertical">
				<Form.Item
					name="district"
					label="구역"
					rules={[
						{
							required: true,
							message: "구역을 선택해주세요",
						},
					]}>
					<Select
						placeholder="구역 선택"
						onChange={handleDistrictChange}>
						{districts.map((district) => (
							<Option
								key={
									district.id
								}
								value={
									district.id
								}>
								{district.name +
									"대 교구"}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="missionary_type"
					label="남여선구분"
					rules={[
						{
							required: true,
							message: "남여선구분을 선택해주세요",
						},
					]}>
					<Select
						placeholder="남여선구분 선택"
						disabled={true}>
						<Option value="남선">
							남선교회
						</Option>
						<Option value="여선">
							여선교회
						</Option>
					</Select>
				</Form.Item>

				<Form.Item
					name="zone"
					label="지구"
					rules={[
						{
							required: true,
							message: "지구를 선택해주세요",
						},
					]}>
					<Select
						placeholder="지구 선택"
						disabled={!selectedDistrict}
						onChange={handleZoneChange}>
						{zones.map((zone) => (
							<Option
								key={zone.id}
								value={zone.id}>
								{zone.name +
									"지구"}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="changed_cell"
					label="구역"
					rules={[
						{
							required: true,
							message: "구역을 선택해주세요",
						},
					]}>
					<Select
						placeholder="구역 선택"
						disabled={!selectedZone}>
						{cells.map((cell) => (
							<Option
								key={cell.id}
								value={cell.id}>
								{cell.name +
									"구역"}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="changed_date"
					label="변경일"
					rules={[
						{
							required: true,
							message: "변경일을 선택해주세요",
						},
					]}>
					<DatePicker
						inputReadOnly={isMobile}
						style={{ width: "100%" }}
						format="YYYY-MM-DD"
						placeholder="변경일 선택"
					/>
				</Form.Item>

				<Form.Item name="reason" label="변경 사유">
					<TextArea
						rows={3}
						placeholder="변경 사유를 입력해주세요"
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default EditCellModal;
